import axios from '../../axios';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { TPostWithAuthor, TError } from '@onesource/schemas';
import { ENTITY } from '../entity';

/**
 * API response shape for posts endpoint
 */
interface PostsResponse {
  posts: TPostWithAuthor[];
  total: number;
}

/**
 * Pagination params for infinite query
 */
interface PostsPageParams {
  limit: number;
  offset: number;
}

const DEFAULT_PAGE_SIZE = 10;

/**
 * Query key for fetching posts with infinite scroll
 */
export const getPostsInfiniteQueryKey = (spaceId: string) => [ENTITY, 'posts', 'infinite', spaceId];

/**
 * Query key for fetching posts with infinite scroll - hook version
 */
export const useGetPostsInfiniteQueryKey = (spaceId: string) => getPostsInfiniteQueryKey(spaceId);

/**
 * Hook to fetch posts with infinite scrolling
 * Returns posts ordered by creation date (newest first)
 *
 * @param spaceId - Space ID (UUID) - should be a feed sub-space
 * @param pageSize - Number of posts per page (default: 10)
 */
export const useGetPostsInfinite = (spaceId: string, pageSize: number = DEFAULT_PAGE_SIZE) => {
  return useInfiniteQuery<PostsResponse, TError>({
    queryKey: useGetPostsInfiniteQueryKey(spaceId),
    queryFn: async ({ pageParam }) => {
      const params = pageParam as PostsPageParams;
      const res = await axios.get<PostsResponse>(`/community-hub/spaces/${spaceId}/posts`, {
        params: {
          limit: params.limit,
          offset: params.offset,
        },
      });
      return res.data;
    },
    initialPageParam: { limit: pageSize, offset: 0 } as PostsPageParams,
    getNextPageParam: (lastPage, allPages) => {
      const loadedCount = allPages.reduce((acc, page) => acc + page.posts.length, 0);
      if (loadedCount >= lastPage.total) {
        return undefined; // No more pages
      }
      return { limit: pageSize, offset: loadedCount } as PostsPageParams;
    },
    enabled: !!spaceId,
    staleTime: 60 * 1000, // 1 minute (posts change frequently)
  });
};
